import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Switch } from "components/DesignSystem/Switch/Switch";
import Table from "components/DesignSystem/Table/V2/Table";
import { useModal } from "hooks/useModal";
import { useMemo, useState, useCallback } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { ReconcileResult } from "store/apis/reconciliation";
import { AddTransactionManually } from "./AddTransactionManually";
import { DateFilter } from "components/Filters/DateFilter";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { formatDate } from "utils/formatDate";
import { useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { Transactions } from "types/Models/books";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { transactionColumn } from "components/Transaction/TransactionsTable";
import { TransactionRowSelection } from "types/Models/transaction";
import { TRANSACTION_COLUMNS } from "constants/transaction";
import { PlusIcon } from "components/icons/PlusIcon";
import TransactionColumn from "components/Transaction/TransactionColumn";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { DividerLine } from "components/icons/DividerLine";
import { Cross } from "components/icons/Cross";
import {
  useChangeTransactionStatusMutation,
  useUpdateTransactionMutation,
} from "store/apis/transactions";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ThreeDots from "static/images/ThreeDots.svg";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useTransactionActions } from "components/Transaction/useTransactionActions";
import { Account } from "types/Models/banks";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";

type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
};

const RECONCILIATION_TXN_PAGE_SIZE = 20;

export const ReconciliationTable = ({
  reconcilTxn,
  sorting,
  setSorting,
  unReconciledTxnIds,
  transactionIdsToReconcile,
  setUnReconciledTxnIds,
  setTransactionIdsToReconcile,
  reconcileAccount,
}: {
  reconcilTxn: ReconcileResult;
  sorting: {
    desc: boolean;
    id: string;
  }[];
  setSorting: React.Dispatch<
    React.SetStateAction<
      {
        desc: boolean;
        id: string;
      }[]
    >
  >;
  unReconciledTxnIds: string[];
  transactionIdsToReconcile: string[];
  setUnReconciledTxnIds: React.Dispatch<React.SetStateAction<string[]>>;
  setTransactionIdsToReconcile: React.Dispatch<React.SetStateAction<string[]>>;
  reconcileAccount?: Account;
}) => {
  const query = useQuery();
  const { update } = useUpdateQuery();
  const entityId = useCurrentEntityId();
  const { successToast, alertToast } = useToast();
  const [rowSelection, setRowSelection] = useState({});
  const [selectedTxnIdForAction, setSelectedTxnIdForAction] = useState("");

  const { Action, modals } = useTransactionActions({
    transactionId: selectedTxnIdForAction,
  });

  const [changeTransactionStatus, { isLoading }] =
    useChangeTransactionStatusMutation();

  const { transactions, entity_bank_account_id } = reconcilTxn || {};
  const data = useMemo(() => transactions || [], [transactions]);

  const pageSize = RECONCILIATION_TXN_PAGE_SIZE;
  const totalPageCount = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPage = Math.min(Number(query.get("page")) || 1, totalPageCount);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize]);

  const {
    open: openAddTxnManuallyModal,
    close: closeAddTxnManuallyModal,
    isOpen: isAddTxnModalManuallyOpen,
  } = useModal();

  const columnHelper = createColumnHelper<Transactions>();

  const onToggleChange = useCallback(
    ({ checked, txn }: { checked: boolean; txn: Transactions }) => {
      const txnId = txn.transaction.uuid;
      const is_reconciled = txn.is_reconciled;

      if (!is_reconciled) {
        if (checked) setTransactionIdsToReconcile((prev) => [...prev, txnId]);
        else
          setTransactionIdsToReconcile((prev) =>
            prev.filter((id) => id !== txnId)
          );
      } else {
        if (!checked) {
          setUnReconciledTxnIds((prev) => [...prev, txnId]);
        } else {
          setUnReconciledTxnIds((prev) => prev.filter((id) => id !== txnId));
        }
      }
    },
    [setUnReconciledTxnIds]
  );

  const changeStatus = useCallback(
    async ({
      transactionStatus,
      transactionId,
    }: {
      transactionStatus: "EXCLUDED" | "RESTORED";
      transactionId: string[];
    }) => {
      try {
        await changeTransactionStatus({
          entityId,
          payload: {
            transaction_ids: transactionId,
            transaction_status: transactionStatus,
            excluded_from_reconciliation: true,
          },
        }).unwrap();
        const message =
          transactionStatus === "EXCLUDED"
            ? "Transaction excluded"
            : "Transaction restored";
        successToast({
          message,
        });
      } catch (error) {
        alertToast(
          { message: (error as BackendError)?.data?.error?.message },
          error as {}
        );
      }
    },
    [changeTransactionStatus, entityId, successToast, alertToast]
  );

  const allSelectedRows: TransactionRowSelection = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([key, value]) => value)
        .map(([key]) => JSON.parse(key)),
    [rowSelection]
  );

  const selectedTransactionIds = useMemo(
    () => allSelectedRows.map((row) => row.uuid).join(","),
    [allSelectedRows]
  );

  const transactionIds = useMemo(
    () => allSelectedRows.map(({ uuid }) => uuid),
    [allSelectedRows]
  );

  const selectedTxns = useMemo(
    () =>
      allSelectedRows
        .map(({ uuid }) => data.find((txn) => txn.transaction.uuid === uuid))
        .filter((txn): txn is Transactions => Boolean(txn)),
    [allSelectedRows, data]
  );

  const handleBulkReconcileToggle = useCallback(
    (shouldReconcile: boolean) => {
      selectedTxns.forEach((txn) => {
        const { transaction, has_linked_invoices, split_transactions } = txn;
        const isExcluded = transaction.status === "EXCLUDED";
        const hasNoCategory =
          !transaction.category?.uuid &&
          !has_linked_invoices &&
          !(split_transactions && split_transactions.length > 0);

        if (isExcluded || hasNoCategory) return;

        onToggleChange({ checked: shouldReconcile, txn });
      });
    },
    [selectedTxns, onToggleChange]
  );

  const columns = useMemo(
    () => [
      transactionColumn.select(selectedTransactionIds),
      transactionColumn.date(),
      columnHelper.accessor("transaction.merchant", {
        id: TRANSACTION_COLUMNS.VENDOR,
        size: 181,
        enableSorting: false,
        header: (header) => <HeadCellContent title="To/From" />,
        cell: (info) => {
          const isExcluded =
            info.row.original.transaction.status === "EXCLUDED";
          return (
            <div>
              <TransactionColumn.Merchant
                info={info}
                showSplitVendors
                disabled={isExcluded}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor("transaction.category", {
        header: () => <HeadCellContent title="Category" />,
        cell: (info) => {
          const isExcluded =
            info.row.original.transaction.status === "EXCLUDED";
          return (
            <TransactionColumn.Category
              info={info}
              truncate
              showSplitActions
              isDisabled={isExcluded}
            />
          );
        },
      }),
      transactionColumn.description(),
      transactionColumn.amount(),

      columnHelper.accessor((row) => row, {
        id: "Include",
        cell: (info) => {
          const {
            transaction,
            is_reconciled,
            has_linked_invoices,
            split_transactions,
          } = info.getValue();
          const isExcluded = transaction.status === "EXCLUDED";
          const hasNoCategory =
            !transaction.category?.uuid &&
            !has_linked_invoices &&
            !(split_transactions && split_transactions.length > 0);
          const txnId = transaction.uuid;

          const isInUnreconciledList = unReconciledTxnIds.includes(txnId);
          const isInReconcileList = transactionIdsToReconcile.includes(txnId);

          let isReconciled = is_reconciled;
          if (!is_reconciled && isInReconcileList) {
            isReconciled = true;
          } else if (is_reconciled && isInUnreconciledList) {
            isReconciled = false;
          }

          const isDisabled = isExcluded || hasNoCategory;

          let tooltipText: string | false = false;
          if (isExcluded) {
            tooltipText =
              "Restore transaction to include in this reconciliation";
          } else if (hasNoCategory) {
            tooltipText =
              "Categorise transaction to include in this reconciliation";
          }

          return (
            <ConditionalToolTip condition={tooltipText}>
              <div className="t-w-full t-flex t-items-center t-justify-center">
                <Switch
                  size="small"
                  checked={isReconciled}
                  onCheckedChange={(checked) =>
                    onToggleChange({ checked, txn: info.getValue() })
                  }
                  disabled={isDisabled}
                />
              </div>
            </ConditionalToolTip>
          );
        },
        header: () => (
          <HeadCellContent
            title="Reconcile"
            infoText="Toggle ON to mark this transaction as reconciled when you confirm."
            align="center"
          />
        ),
      }),

      columnHelper.accessor("transaction", {
        id: TRANSACTION_COLUMNS.OPTIONS,
        size: 43,
        header: () => <HeadCellContent title="" />,
        enableSorting: false,
        cell: (info) => {
          const txn = info.row.original;
          const txnId = txn.transaction.uuid;
          const isExcluded = txn.transaction.status === "EXCLUDED";
          const isManualTransaction = Boolean(txn?.transaction?.journal_entry);

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown.Root>
                <Dropdown.Trigger asChild>
                  <div>
                    <Button size="xsmall" customType="ghost_icon">
                      <img
                        src={ThreeDots}
                        alt="Action"
                        className="t-select-none"
                      />
                    </Button>
                  </div>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content
                    sideOffset={8}
                    side="bottom"
                    className="t-mr-16"
                    align="start"
                  >
                    {isExcluded ? (
                      <Dropdown.Item
                        onSelect={() =>
                          changeStatus({
                            transactionStatus: "RESTORED",
                            transactionId: [txnId],
                          })
                        }
                      >
                        Restore transaction
                      </Dropdown.Item>
                    ) : (
                      <Dropdown.Item
                        onSelect={() =>
                          changeStatus({
                            transactionStatus: "EXCLUDED",
                            transactionId: [txnId],
                          })
                        }
                      >
                        Exclude transaction
                      </Dropdown.Item>
                    )}
                    {isManualTransaction && (
                      <div onClick={() => setSelectedTxnIdForAction(txnId)}>
                        <Action.Delete />
                      </div>
                    )}
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            </div>
          );
        },
      }),
    ],
    [
      selectedTransactionIds,
      unReconciledTxnIds,
      transactionIdsToReconcile,
      Action,
      changeStatus,
      columnHelper,
      onToggleChange,
      setSelectedTxnIdForAction,
    ]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => row.original.transaction.status !== "EXCLUDED",
    manualSorting: true,
    state: {
      rowSelection,
      sorting,
    },
    // @ts-ignore
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getRowId: (row) =>
      JSON.stringify({
        uuid: row.transaction.uuid,
        category_id: row.transaction.category?.uuid,
        id: row.transaction.uuid,
        amount: row.transaction.amount,
        source: row.transaction.source,
        date: row.transaction.date,
        vendor: {
          logo: row.transaction.logo,
          name: row.transaction.merchant,
        },
        isCreditCard: row.transaction.is_credit_card,
      }),
  });

  const { isMobile } = useWindowSize();

  const addTxnButton = (
    <Button
      size="small"
      onClick={openAddTxnManuallyModal}
      leftIcon={<PlusIcon color="currentColor" size="8" />}
    >
      {isMobile ? "Add transaction" : "Add transaction manually"}
    </Button>
  );

  const { values, updateFilter } = useFilters<FilterValues>({
    initialValue: {
      START_DATE: query.get("START_DATE")!,
      END_DATE: query.get("END_DATE")!,
      SELECT_PERIOD: "",
    },
    useQueryParams: true,
  });

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-flex t-justify-between t-items-center sm:t-flex-col sm:t-items-stretch sm:t-gap-2">
        <div className="t-flex t-items-center t-gap-1 t-flex-wrap sm:t-flex-col sm:t-items-start sm:t-gap-1">
          <span className="t-text-body-sm t-text-text-30">
            Showing unreconciled transactions for:
          </span>

          <Filter.Root
            defaultValue="PERIOD"
            capsule={
              <>
                {Boolean(values?.START_DATE) && Boolean(values?.END_DATE) && (
                  <Chip
                    isRemovable={false}
                    isActive
                    filterType="PERIOD"
                    size={isMobile ? "small" : "xsmall"}
                  >
                    {formatDate(values.START_DATE)}
                    {" - "}
                    {formatDate(values.END_DATE)}
                  </Chip>
                )}
              </>
            }
            trigger={<div className="t-w-0 t-h-8 t-overflow-hidden" />}
            hideDivider
          >
            <Filter.Portal>
              <Filter.List>
                <Filter.ListItem value="PERIOD">Period</Filter.ListItem>
              </Filter.List>

              <Filter.Body value="PERIOD" block>
                <DateFilter
                  updateFilter={updateFilter}
                  values={values}
                  showPeriodSelector={false}
                  minStartDate={
                    new Date(reconcileAccount?.reconcilation_start_date ?? "")
                  }
                  maxEndDate={new Date(reconcilTxn.reconciliation_end_date)}
                />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
        </div>
        {!isMobile && addTxnButton}
      </div>
      <div className="t-flex t-justify-end sm:t-justify-between sm:t-items-center">
        {isMobile && addTxnButton}
        <PaginationHandler
          page_num={currentPage}
          per_page={pageSize}
          total_count={data.length}
          total_pages={totalPageCount}
          onPageNumChange={(page) => update({ query: "page", value: page })}
        />
      </div>
      <Table.Container className="t-mb-20" layout="fixed">
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.HeadRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: header.getSize() }}
                    isUpperCase
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.HeadRow>
            ))}
          </Table.Head>
          <Table.Body>
            {table.getRowModel().rows.map((row) => {
              const isExcluded = row.original.transaction.status === "EXCLUDED";
              const isRowsSelected = row.getIsSelected();

              return (
                <Table.Row
                  key={row.id}
                  className={classNames(
                    "hover:t-bg-surface-lighter-grey t-group",
                    {
                      "t-text-text-100": !isExcluded,
                      "t-text-text-30": isExcluded,
                    }
                  )}
                  isRowSelected={isRowsSelected}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isOptionsColumn =
                      cell.column.id === TRANSACTION_COLUMNS.OPTIONS;

                    return (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        className={classNames({
                          "t-opacity-50": isExcluded && !isOptionsColumn,
                        })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Content>
      </Table.Container>
      {modals}
      <AddTransactionManually
        isOpen={isAddTxnModalManuallyOpen}
        onClose={closeAddTxnManuallyModal}
        selectedAccountId={entity_bank_account_id || ""}
      />
      <div className="[&>div]:!t-bottom-[70px]">
        <SelectedFloatingControl>
          {allSelectedRows.length > 0 && (
            <>
              <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
                {allSelectedRows.length} selected
              </span>
              <span className="t-text-neutral-10 t-h-full t-flex">
                <DividerLine />
              </span>
              <span>
                <Button
                  size="small"
                  onClick={() => handleBulkReconcileToggle(true)}
                >
                  Mark as reconciled
                </Button>
              </span>
              <span>
                <Button
                  size="small"
                  onClick={() => handleBulkReconcileToggle(false)}
                >
                  Mark as unreconciled
                </Button>
              </span>
              <span className="t-text-neutral-10 t-h-full t-flex">
                <DividerLine />
              </span>
              <span>
                <Button
                  size="small"
                  onClick={() =>
                    changeStatus({
                      transactionStatus: "EXCLUDED",
                      transactionId: transactionIds,
                    })
                  }
                  disabled={isLoading}
                  isLoading={isLoading}
                >
                  Exclude transactions
                </Button>
              </span>

              <Button
                customType="ghost_icon"
                size="xsmall"
                onClick={() => table.resetRowSelection()}
              >
                <Cross />
              </Button>
            </>
          )}
        </SelectedFloatingControl>
      </div>
    </div>
  );
};
